import styled from "styled-components";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { AreaLightGraphCell, ChangeCell, ProgressBarWithValueCell, TextCell } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes, colorsSets } from "@similarweb/styles";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";

import type { AudienceTableRow } from "../types";

const Bullet = styled(Box)<{ $color: string }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) => props.$color || colorsSets.c["midnight 300"]};
`;

const EmptyColumn = { width: 12 };

const formatters = {
    percentage: percentageSignFilter(),
    abbrNumber: abbrNumberFilter(),
};

export const columnConfig: ColumnsType<AudienceTableRow> = [
    EmptyColumn,
    {
        dataIndex: "categoryName",
        minWidth: 250,
        get title() {
            return translate("common.industry");
        },
        render: (categoryName: AudienceTableRow["categoryName"], row) => (
            <Flex
                alignItems="center"
                gap="8px"
            >
                <Bullet $color={row.color} />
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[500]}
                >
                    {categoryName}
                </Text>
            </Flex>
        ),
    },
    {
        dataIndex: "uniqueVisitors",
        minWidth: 250,
        get title() {
            return translate("common.tables.columns.unique.visits.title");
        },
        render: ({ share, value }: AudienceTableRow["uniqueVisitors"], row) => (
            <ProgressBarWithValueCell
                share={share}
                valueMinWidth="48px"
                color={row.color as StrictColor}
                value={formatters.abbrNumber(value) as string}
            />
        ),
    },
    {
        dataIndex: "periodChange",
        width: 90,
        align: "right",
        get title() {
            return translate("common.yoy.change");
        },
        render: (periodChange: AudienceTableRow["periodChange"]) => (
            <Flex justifyContent="flex-end">
                <ChangeCell
                    value={periodChange}
                    formattedValue={formatters.percentage(periodChange, 2) as string}
                />
            </Flex>
        ),
    },
    {
        dataIndex: "trend",
        width: 105,
        get title() {
            return translate("common.tables.columns.trend.title");
        },
        render: (trend: AudienceTableRow["trend"]) => <AreaLightGraphCell data={trend} />,
    },
    {
        dataIndex: "score",
        align: "right",
        get title() {
            return translate("industry.overview.marketsize.widget.score.title");
        },
        render: (score: AudienceTableRow["score"]) => <TextCell>{formatters.percentage(score, 2)}</TextCell>,
    },
    EmptyColumn,
];
